import React, { useState, useEffect } from 'react';
import { menuService } from '../../../services/menuService';
import { useAuth } from '../../Admin/context/AuthContext';   // ✅ sesuaikan path
import {
    FaPlus, FaEdit, FaTrash, FaSearch, FaList,
    FaTimes, FaSave, FaCheckCircle, FaExclamationTriangle,
    FaChevronDown, FaChevronRight, FaFolder, FaFile,
    FaEye, FaEyeSlash, FaToggleOn, FaToggleOff,
    FaShieldAlt, FaArrowUp, FaArrowDown
} from 'react-icons/fa';

// Icon mapping untuk pilihan ikon
const iconOptions = [
    { value: 'dashboard', label: 'Dashboard', icon: '📊' },
    { value: 'users', label: 'Users', icon: '👥' },
    { value: 'shield', label: 'Shield', icon: '🛡️' },
    { value: 'menu', label: 'Menu', icon: '📋' },
    { value: 'profil', label: 'Profil', icon: '👤' },
    { value: 'artikel', label: 'Artikel', icon: '📰' },
    { value: 'layanan', label: 'Layanan', icon: '⚙️' },
    { value: 'home', label: 'Home', icon: '🏠' },
    { value: 'file', label: 'File', icon: '📄' },
    { value: 'chart', label: 'Chart', icon: '📈' },
    { value: 'settings', label: 'Settings', icon: '🔧' },
    { value: 'info', label: 'Info', icon: 'ℹ️' },
];

const ManajemenMenu = () => {
    const [menus, setMenus] = useState([]);
    const [flatMenus, setFlatMenus] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedItems, setExpandedItems] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [editingMenu, setEditingMenu] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [notification, setNotification] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);

    const [formData, setFormData] = useState({
        name: '',
        route: '',
        icon: 'file',
        parent_id: '',
        order: 0,
        is_active: true,
        is_visible: true,
        roles: []
    });

    const [errors, setErrors] = useState({});

    // ✅ Ambil user dari AuthContext
    const { user } = useAuth();

    // ✅ Helper cek permission
    const hasPermission = (permission) => {
        if (!user) return false;
        if (user?.roles?.includes('admin')) return true;
        if (!user?.permissions) return false;
        return user.permissions.includes(permission);
    };

    // Load menus
    const loadMenus = async () => {
        setLoading(true);
        const result = await menuService.getMenus();
        if (result.success) {
            setMenus(result.data || []);
            setFlatMenus(flattenMenus(result.data || []));
        } else {
            showNotification(result.message, 'error');
        }
        setLoading(false);
    };

    // Load roles
    const loadRoles = async () => {
        const result = await menuService.getRoles();
        if (result.success) {
            setRoles(result.data || []);
        }
    };

    useEffect(() => {
        loadMenus();
        loadRoles();
    }, []);

    // Flatten menu tree for dropdown
    const flattenMenus = (menuTree, level = 0) => {
        let result = [];
        menuTree.forEach(menu => {
            result.push({
                ...menu,
                level,
                displayName: `${'—'.repeat(level)} ${menu.name}`
            });
            if (menu.children && menu.children.length > 0) {
                result = result.concat(flattenMenus(menu.children, level + 1));
            }
        });
        return result;
    };

    // Show notification
    const showNotification = (message, type = 'success') => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 3000);
    };

    // Toggle expand/collapse
    const toggleExpand = (menuId) => {
        setExpandedItems(prev =>
            prev.includes(menuId)
                ? prev.filter(id => id !== menuId)
                : [...prev, menuId]
        );
    };

    // Expand all
    const expandAll = () => {
        const allIds = flatMenus.filter(m => m.children && m.children.length > 0).map(m => m.id);
        setExpandedItems(allIds);
    };

    // Collapse all
    const collapseAll = () => {
        setExpandedItems([]);
    };

    // Open create form
    const handleCreate = (parentId = '') => {
        setEditingMenu(null);
        setFormData({
            name: '',
            route: '',
            icon: 'file',
            parent_id: parentId,
            order: 0,
            is_active: true,
            is_visible: true,
            roles: []
        });
        setErrors({});
        setShowForm(true);
    };

    // Open edit form
    const handleEdit = (menu) => {
        setEditingMenu(menu);
        setFormData({
            name: menu.name || '',
            route: menu.route || '',
            icon: menu.icon || 'file',
            parent_id: menu.parent_id || '',
            order: menu.order || 0,
            is_active: menu.is_active === true || menu.is_active === 1,
            is_visible: menu.is_visible === true || menu.is_visible === 1,
            roles: menu.roles ? menu.roles.map(r => r.id) : []
        });
        setErrors({});
        setShowForm(true);
    };

    // Handle input change
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    // Handle role checkbox
    const handleRoleChange = (roleId) => {
        setFormData(prev => {
            const roles = prev.roles.includes(roleId)
                ? prev.roles.filter(id => id !== roleId)
                : [...prev.roles, roleId];
            return { ...prev, roles };
        });
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) {
            newErrors.name = 'Nama menu wajib diisi';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle submit
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setSubmitting(true);

        const submitData = {
            ...formData,
            parent_id: formData.parent_id || null,
            order: parseInt(formData.order) || 0
        };

        let result;
        if (editingMenu) {
            result = await menuService.updateMenu(editingMenu.id, submitData);
        } else {
            result = await menuService.createMenu(submitData);
        }

        if (result.success) {
            showNotification(result.message || 'Menu berhasil disimpan', 'success');
            setShowForm(false);
            loadMenus();
        } else {
            showNotification(result.message || 'Gagal menyimpan menu', 'error');
        }
        setSubmitting(false);
    };

    // Handle delete
    const handleDelete = (menu) => {
        setDeleteConfirm(menu);
    };

    const confirmDelete = async () => {
        if (!deleteConfirm) return;
        const result = await menuService.deleteMenu(deleteConfirm.id);
        if (result.success) {
            showNotification('Menu berhasil dihapus', 'success');
            loadMenus();
        } else {
            showNotification(result.message, 'error');
        }
        setDeleteConfirm(null);
    };

    // Filter menus for search
    const filterMenus = (menuList) => {
        if (!searchTerm) return menuList;

        return menuList.filter(menu => {
            const matchSelf = menu.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (menu.route && menu.route.toLowerCase().includes(searchTerm.toLowerCase()));
            const childrenMatch = menu.children ? filterMenus(menu.children).length > 0 : false;
            return matchSelf || childrenMatch;
        }).map(menu => ({
            ...menu,
            children: menu.children ? filterMenus(menu.children) : []
        }));
    };

    const filteredMenus = filterMenus(menus);

    // ✅ Render menu tree dengan permission check
    const renderMenuTree = (menuList, level = 0) => {
        return menuList.map((menu) => {
            const hasChildren = menu.children && menu.children.length > 0;
            const isExpanded = expandedItems.includes(menu.id);

            return (
                <div key={menu.id}>
                    <div
                        className={`flex items-center justify-between p-3 rounded-lg mb-2 transition-all ${level === 0
                            ? 'bg-gradient-to-r from-[#0F4C3A] to-[#2E7D32] text-white'
                            : 'bg-white border border-gray-200 hover:border-[#2E7D32]'
                            }`}
                        style={{ marginLeft: level * 20 }}
                    >
                        <div className="flex items-center flex-1">
                            {/* Expand/Collapse Button */}
                            {hasChildren && (
                                <button
                                    onClick={() => toggleExpand(menu.id)}
                                    className={`mr-2 p-1 rounded ${level === 0 ? 'text-white' : 'text-gray-500 hover:bg-gray-100'}`}
                                >
                                    {isExpanded ? <FaChevronDown size={14} /> : <FaChevronRight size={14} />}
                                </button>
                            )}

                            {/* Icon */}
                            <span className="text-xl mr-3">
                                {hasChildren ? <FaFolder /> : <FaFile />}
                            </span>

                            {/* Menu Info */}
                            <div>
                                <p className={`font-medium ${level === 0 ? 'text-white' : 'text-gray-700'}`}>
                                    {menu.name}
                                </p>
                                {menu.route && (
                                    <p className={`text-xs ${level === 0 ? 'text-white/70' : 'text-gray-500'}`}>
                                        {menu.route}
                                    </p>
                                )}
                            </div>

                            {/* Status Badges */}
                            <div className="ml-4 flex items-center space-x-2">
                                {menu.is_active === 1 || menu.is_active === true ? (
                                    <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full">Aktif</span>
                                ) : (
                                    <span className="bg-gray-100 text-gray-500 text-xs px-2 py-0.5 rounded-full">Nonaktif</span>
                                )}
                                {menu.is_visible === 1 || menu.is_visible === true ? (
                                    <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full">Visible</span>
                                ) : (
                                    <span className="bg-gray-100 text-gray-500 text-xs px-2 py-0.5 rounded-full">Hidden</span>
                                )}
                            </div>
                        </div>

                        {/* ✅ Actions — dengan permission check */}
                        <div className="flex items-center space-x-1 ml-4">
                            {/* Tambah Submenu */}
                            {hasChildren && hasPermission('menus.create') && (
                                <button
                                    onClick={() => handleCreate(menu.id)}
                                    className={`p-2 rounded-lg transition-colors ${level === 0
                                        ? 'text-white/80 hover:bg-white/20'
                                        : 'text-green-500 hover:bg-green-50'
                                        }`}
                                    title="Tambah Submenu"
                                >
                                    <FaPlus size={14} />
                                </button>
                            )}

                            {/* Edit */}
                            {hasPermission('menus.update') && (
                                <button
                                    onClick={() => handleEdit(menu)}
                                    className={`p-2 rounded-lg transition-colors ${level === 0
                                        ? 'text-white/80 hover:bg-white/20'
                                        : 'text-blue-500 hover:bg-blue-50'
                                        }`}
                                    title="Edit"
                                >
                                    <FaEdit size={14} />
                                </button>
                            )}

                            {/* Hapus */}
                            {hasPermission('menus.delete') && (
                                <button
                                    onClick={() => handleDelete(menu)}
                                    className={`p-2 rounded-lg transition-colors ${level === 0
                                        ? 'text-white/80 hover:bg-white/20'
                                        : 'text-red-500 hover:bg-red-50'
                                        }`}
                                    title="Hapus"
                                >
                                    <FaTrash size={14} />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Render Children */}
                    {hasChildren && isExpanded && (
                        <div className="mb-2">
                            {renderMenuTree(menu.children, level + 1)}
                        </div>
                    )}
                </div>
            );
        });
    };

    return (
        <div>
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-[#0F4C3A]">Manajemen Menu</h2>
                    <p className="text-gray-600">Kelola menu navigasi dan hak akses</p>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={expandAll}
                        className="flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                        <FaChevronDown className="mr-2" /> Expand All
                    </button>
                    <button
                        onClick={collapseAll}
                        className="flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                        <FaChevronRight className="mr-2" /> Collapse All
                    </button>

                    {/* ✅ Tombol Tambah Menu — cek permission */}
                    {hasPermission('menus.create') && (
                        <button
                            onClick={() => handleCreate()}
                            className="btn-primary flex items-center justify-center"
                        >
                            <FaPlus className="mr-2" />
                            Tambah Menu
                        </button>
                    )}
                </div>
            </div>

            {/* Notification */}
            {notification && (
                <div className={`mb-6 p-4 rounded-lg flex items-center ${notification.type === 'success'
                    ? 'bg-green-50 border border-green-200 text-green-700'
                    : 'bg-red-50 border border-red-200 text-red-700'
                    }`}>
                    {notification.type === 'success' ? (
                        <FaCheckCircle className="mr-3 flex-shrink-0" />
                    ) : (
                        <FaExclamationTriangle className="mr-3 flex-shrink-0" />
                    )}
                    {notification.message}
                </div>
            )}

            {/* Search Bar */}
            <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
                <div className="relative">
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Cari menu..."
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent"
                    />
                </div>
            </div>

            {/* Menu Tree */}
            <div className="bg-white rounded-xl shadow-sm p-4">
                {loading ? (
                    <div className="text-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2E7D32] mx-auto"></div>
                    </div>
                ) : filteredMenus.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                        Tidak ada data menu
                    </div>
                ) : (
                    renderMenuTree(filteredMenus)
                )}
            </div>

            {/* Form Modal */}
            {showForm && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            {/* Modal Header */}
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-xl font-bold text-[#0F4C3A] flex items-center">
                                    <FaList className="mr-2 text-[#2E7D32]" />
                                    {editingMenu ? 'Edit Menu' : 'Tambah Menu Baru'}
                                </h3>
                                <button
                                    onClick={() => setShowForm(false)}
                                    className="text-gray-400 hover:text-gray-600 p-1 hover:bg-gray-100 rounded-full"
                                >
                                    <FaTimes size={20} />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-4">
                                {/* Nama Menu */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Nama Menu <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent ${errors.name ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                            }`}
                                        placeholder="Masukkan nama menu"
                                    />
                                    {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                                </div>

                                {/* Route */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Route
                                    </label>
                                    <input
                                        type="text"
                                        name="route"
                                        value={formData.route}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent"
                                        placeholder="Contoh: /admin/users"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    {/* Icon */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Icon
                                        </label>
                                        <select
                                            name="icon"
                                            value={formData.icon}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent"
                                        >
                                            {iconOptions.map(opt => (
                                                <option key={opt.value} value={opt.value}>
                                                    {opt.icon} {opt.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Order */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Order
                                        </label>
                                        <input
                                            type="number"
                                            name="order"
                                            value={formData.order}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent"
                                            placeholder="0"
                                        />
                                    </div>
                                </div>

                                {/* Parent Menu */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Parent Menu
                                    </label>
                                    <select
                                        name="parent_id"
                                        value={formData.parent_id}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent"
                                    >
                                        <option value="">Tidak ada (Menu Utama)</option>
                                        {flatMenus
                                            .filter(m => m.id !== editingMenu?.id)
                                            .map(menu => (
                                                <option key={menu.id} value={menu.id}>
                                                    {menu.displayName}
                                                </option>
                                            ))}
                                    </select>
                                </div>

                                {/* Status Toggles */}
                                <div className="grid grid-cols-2 gap-4">
                                    <label className="flex items-center p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                                        <input
                                            type="checkbox"
                                            name="is_active"
                                            checked={formData.is_active}
                                            onChange={handleInputChange}
                                            className="mr-3 text-[#2E7D32] focus:ring-[#2E7D32] w-5 h-5"
                                        />
                                        <div>
                                            <span className="text-sm font-medium text-gray-700">Menu Aktif</span>
                                            <p className="text-xs text-gray-500">Dapat diakses user</p>
                                        </div>
                                    </label>

                                    <label className="flex items-center p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                                        <input
                                            type="checkbox"
                                            name="is_visible"
                                            checked={formData.is_visible}
                                            onChange={handleInputChange}
                                            className="mr-3 text-[#2E7D32] focus:ring-[#2E7D32] w-5 h-5"
                                        />
                                        <div>
                                            <span className="text-sm font-medium text-gray-700">Menu Visible</span>
                                            <p className="text-xs text-gray-500">Tampil di navigasi</p>
                                        </div>
                                    </label>
                                </div>

                                {/* Roles Access */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Hak Akses Role
                                    </label>
                                    <div className="space-y-2">
                                        {roles.map((role) => (
                                            <label key={role.id} className="flex items-center p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.roles.includes(role.id)}
                                                    onChange={() => handleRoleChange(role.id)}
                                                    className="mr-3 text-[#2E7D32] focus:ring-[#2E7D32] w-5 h-5"
                                                />
                                                <FaShieldAlt className="text-gray-400 mr-2" />
                                                <span className="text-sm font-medium text-gray-700 capitalize">{role.name}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                {/* Buttons */}
                                <div className="flex justify-end space-x-3 pt-4 border-t">
                                    <button
                                        type="button"
                                        onClick={() => setShowForm(false)}
                                        className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                                    >
                                        Batal
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className={`flex items-center bg-gradient-to-r from-[#0F4C3A] to-[#2E7D32] text-white px-6 py-2.5 rounded-lg font-semibold hover:shadow-lg transition-all ${submitting ? 'opacity-75 cursor-not-allowed' : ''
                                            }`}
                                    >
                                        {submitting ? (
                                            <>
                                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                                Menyimpan...
                                            </>
                                        ) : (
                                            <>
                                                <FaSave className="mr-2" />
                                                {editingMenu ? 'Update Menu' : 'Simpan Menu'}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteConfirm && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
                        <div className="text-center">
                            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <FaTrash className="text-3xl text-red-500" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-800 mb-2">Hapus Menu?</h3>
                            <p className="text-gray-600 mb-6">
                                Anda akan menghapus menu <strong>{deleteConfirm.name}</strong>.
                                Submenu terkait juga akan dihapus.
                            </p>
                            <div className="flex justify-center space-x-3">
                                <button
                                    onClick={() => setDeleteConfirm(null)}
                                    className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                                >
                                    Batal
                                </button>
                                <button
                                    onClick={confirmDelete}
                                    className="px-6 py-2.5 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600 transition-colors"
                                >
                                    Hapus
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManajemenMenu;